import React, { useEffect, useRef, useState } from 'react';
import InvoiceLink from './InvoiceLink';
import { Phone, RefreshCw } from 'lucide-react';
import marketApi from './marketApi';

/**
 * Suivi d'une commande de livraison — timeline de statuts, livreur (appel /
 * WhatsApp), CODE DE LIVRAISON (remis au livreur à la réception), annulation
 * tant que la commande n'est pas prête. Rafraîchissement automatique (10 s)
 * tant que la commande est active — pas de socket sur le web, le polling suffit.
 */
const FLOW = ['pending', 'accepted', 'preparing', 'ready', 'picked_up', 'delivering', 'delivered'];
const LABELS = {
  pending: 'Commande envoyée', accepted: 'Acceptée par le commerce', preparing: 'En préparation',
  ready: 'Prête — en attente du livreur', picked_up: 'Récupérée par le livreur',
  delivering: 'En route vers vous', delivered: 'Livrée',
};
const ACTIVE = ['pending', 'accepted', 'preparing', 'ready', 'picked_up', 'delivering'];

export default function OrderTrackingPage({ orderId, nav }) {
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const timer = useRef(null);

  const load = async () => {
    try {
      const r = await marketApi.orderDetail(orderId);
      if (r.success) { setOrder(r.order); setErr(null); }
      else setErr(r.error || 'Commande introuvable');
    } catch (e) { setErr(e?.error || e?.message || 'Erreur réseau'); }
  };

  useEffect(() => {
    load();
    timer.current = setInterval(() => {
      setOrder(prev => {
        if (prev && !ACTIVE.includes(prev.status)) { clearInterval(timer.current); return prev; }
        load();
        return prev;
      });
    }, 10000);
    return () => clearInterval(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const cancel = async () => {
    if (!window.confirm('Annuler cette commande ?')) return;
    setCancelling(true);
    try {
      const r = await marketApi.cancelOrder(orderId);
      if (!r.success) alert(r.error || 'Annulation impossible');
      await load();
    } catch (e) { alert(e?.error || e?.message || 'Annulation impossible'); }
    finally { setCancelling(false); }
  };

  const call = (p) => { if (p) window.open(`tel:${p}`); };
  const wa = (p) => {
    if (!p) return;
    const c = p.replace(/\D/g, '');
    window.open(`https://wa.me/${c.startsWith('243') ? c : `243${c.replace(/^0/, '')}`}`);
  };

  if (err) return <div className="mk-container"><div className="mk-center">{err}</div></div>;
  if (!order) return <div className="mk-center"><div className="mk-spin" /> Chargement…</div>;

  const idx = FLOW.indexOf(order.status);
  const isCancelled = order.status === 'cancelled' || order.status === 'failed';
  // Annulation possible tant que la commande n'est pas « en préparation » (règle serveur, 2026-10-24)
  const canCancel = ['pending', 'accepted'].includes(order.status);

  return (
    <div className="mk-container">
      <div className="mk-two">
        <div>
          {/* Timeline */}
          <div className="mk-panel">
            <h3>
              Commande #{(order.order_public_id || order.order_number || order.order_id).toString().slice(-8)}
              <button className="mk-iconbtn" style={{ float: 'right', width: 32, height: 32 }} onClick={load} aria-label="Actualiser"><RefreshCw size={15} /></button>
            </h3>
            <InvoiceLink type="order" id={order.order_id} compact />
            {order.is_pharmacy && order.rx_status === 'pending_review' && order.status === 'pending' && (
              <p className="mk-note" style={{ marginBottom: 10 }}>📋 La pharmacie examine votre ordonnance. Vous serez prévenu dès qu'elle l'aura validée.</p>
            )}
            {order.is_pharmacy && order.rx_status === 'rejected' && (
              <p className="mk-note err" style={{ marginBottom: 10 }}>❌ Ordonnance refusée par la pharmacie : {order.rx_rejection_reason}</p>
            )}
            {order.is_pharmacy && order.rx_original_return && !['delivered', 'cancelled', 'refunded'].includes(order.status) && (
              <p className="mk-note warn" style={{ marginBottom: 10 }}>📄 Préparez votre ordonnance ORIGINALE : remettez-la au livreur, il la rapporte à la pharmacie.</p>
            )}
            {isCancelled ? (
              <p className="mk-note err">Commande {order.status === 'failed' ? 'en échec de livraison' : 'annulée'}.
                {order.payment_status === 'refunded' && ' Votre paiement a été remboursé sur votre solde Mbiyo.'}</p>
            ) : (
              <div className="mk-steps">
                {FLOW.map((s, i) => (
                  <div key={s} className={`mk-step${i < idx ? ' done' : i === idx ? ' now' : ''}`}>
                    <div className="rail"><div className="dot" />{i < FLOW.length - 1 && <div className="bar" />}</div>
                    <div className="lbl">{LABELS[s]}</div>
                  </div>
                ))}
              </div>
            )}
            {canCancel && (
              <button className="mk-btn ghost" style={{ color: 'var(--mb-danger)' }} disabled={cancelling} onClick={cancel}>
                {cancelling ? 'Annulation…' : 'Annuler la commande'}
              </button>
            )}
          </div>

          {/* Livreur */}
          {order.driver && !isCancelled && (
            <div className="mk-panel" style={{ marginTop: 16 }}>
              <h3>🛵 Votre livreur</h3>
              <div className="mk-line">
                {order.driver.photo ? <img src={order.driver.photo} alt="" style={{ borderRadius: '50%' }} /> : <div className="mk-card-ph" style={{ width: 58, height: 58, borderRadius: '50%', background: 'var(--mb-surface-alt)' }}>🛵</div>}
                <div className="info">
                  <div className="name">{order.driver.name}</div>
                  {order.store_delivery && (
                    <div style={{ fontSize: '.76rem', fontWeight: 700, color: 'var(--mb-success)', background: 'var(--mb-success-tint)', borderRadius: 7, padding: '2px 8px', display: 'inline-block', marginTop: 2 }}>
                      🛵 Livré par l'équipe de {order.supplier?.business_name || 'votre commerce'}
                    </div>
                  )}
                  <div className="opt">{order.driver.vehicle?.plate || ''}</div>
                </div>
                <button className="mk-iconbtn" onClick={() => call(order.driver.phone)} aria-label="Appeler"><Phone size={17} /></button>
                <button className="mk-iconbtn" style={{ background: '#25D366', color: '#fff' }} onClick={() => wa(order.driver.phone)} aria-label="WhatsApp">W</button>
              </div>
            </div>
          )}

          {/* Code de livraison */}
          {!isCancelled && order.delivery_code && ['picked_up', 'delivering', 'ready'].includes(order.status) && (
            <div className="mk-code">
              <b>{order.delivery_code}</b>
              <p>Code de livraison — donnez-le au livreur uniquement à la réception de votre commande.</p>
            </div>
          )}
        </div>

        {/* Détail */}
        <div className="mk-panel">
          <h3>{order.supplier?.business_name || 'Commande'}</h3>
          {(order.items || []).map((it, i) => (
            <div className="mk-total" key={i} style={{ alignItems: 'flex-start' }}>
              <span>
                {it.quantity}× {it.product_name}
                {(Array.isArray(it.selected_extras) ? it.selected_extras : []).filter(o => o && o.name).map((o, j) => (
                  <span key={j} style={{ display: 'block', fontSize: '.78rem', color: 'var(--mb-text-3)' }}>+ {o.name}{o.price_delta ? ` (+${o.price_delta.toLocaleString('fr-FR')} FC)` : ''}</span>
                ))}
                {it.special_instructions && <span style={{ display: 'block', fontSize: '.78rem', color: 'var(--mb-text-3)' }}>📝 {it.special_instructions}</span>}
              </span>
              <span>{(it.total_price || 0).toLocaleString('fr-FR')} FC</span>
            </div>
          ))}
          <div className="mk-total" style={{ borderTop: '1px solid var(--mb-border)', marginTop: 6, paddingTop: 10 }}>
            <span>Sous-total</span><span>{(order.subtotal || 0).toLocaleString('fr-FR')} FC</span>
          </div>
          <div className="mk-total"><span>Livraison</span><span>{(order.delivery_fee || 0).toLocaleString('fr-FR')} FC</span></div>
          {order.rx_return_fee > 0 && <div className="mk-total"><span>📄 Retour de l'ordonnance</span><span>{order.rx_return_fee.toLocaleString('fr-FR')} FC</span></div>}
          {(order.mm_surcharge || 0) > 0 && <div className="mk-total"><span>Frais Mobile Money</span><span>{order.mm_surcharge.toLocaleString('fr-FR')} FC</span></div>}
          <div className="mk-total grand"><span>Total</span><span>{(order.total || 0).toLocaleString('fr-FR')} FC</span></div>
          <p className="mk-note" style={{ marginTop: 10 }}>
            📍 {order.delivery?.address}{order.delivery?.details ? ` — ${order.delivery.details}` : ''}<br />
            Paiement : {order.payment_method === 'cash' ? 'Espèces à la livraison' : order.payment_method === 'balance' ? 'Solde Mbiyo' : 'Mobile Money'}
            {order.payment_status === 'paid' ? ' (payé ✓)' : ''}
          </p>
          <button className="mk-btn ghost block" style={{ marginTop: 10 }} onClick={() => nav({ page: 'orders' })}>← Mes commandes</button>
        </div>
      </div>
    </div>
  );
}
